import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { GET, POST, PUT, DELETE, loading, alert, format_date, toFixed, required, Select, DateInput } from "../components/CustomComponent";

const API = "v1/admin/ma-contracts";

// เกณฑ์เตือนใกล้หมดสัญญา (วัน) — ใช้เมื่อสัญญาไม่ได้ตั้ง notice_days ไว้เอง
const DEFAULT_NOTICE_DAYS = 60;

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// นับวันแบบตัดเวลาออก เพื่อไม่ให้ timezone ทำให้ผลลัพธ์เพี้ยนไป 1 วัน
const daysBetween = (fromIso, toIso) =>
  Math.round((new Date(`${toIso}T00:00:00`) - new Date(`${fromIso}T00:00:00`)) / 86400000);

const showDate = (iso) => (iso ? format_date(iso, "dd/mm/yyyy") : "—");
const money = (value) => `฿${toFixed(Number(value) || 0)}`;

const STATUS_OPTIONS = [
  { value: "Active", label: "ใช้งานอยู่ (Active)" },
  { value: "Expiring", label: "ใกล้หมดอายุ (Expiring)" },
  { value: "Expired", label: "หมดอายุแล้ว (Expired)" },
  { value: "Cancelled", label: "ยกเลิกสัญญา (Cancelled)" },
];

const STATUS_STYLE = {
  Active: { background: "rgba(15, 107, 220, 0.14)", color: "#0b4fa0", label: "ใช้งานอยู่" },
  Expiring: { background: "rgba(245, 158, 11, 0.18)", color: "#b45309", label: "ใกล้หมดอายุ" },
  Expired: { background: "rgba(100, 116, 139, 0.20)", color: "#475569", label: "หมดอายุแล้ว" },
  Cancelled: { background: "rgba(239, 68, 68, 0.16)", color: "#b91c1c", label: "ยกเลิก" },
};

const BILLING_OPTIONS = [
  { value: "monthly", label: "รายเดือน" },
  { value: "quarterly", label: "ราย 3 เดือน" },
  { value: "semiannual", label: "ราย 6 เดือน" },
  { value: "yearly", label: "รายปี" },
  { value: "onetime", label: "จ่ายครั้งเดียว" },
  { value: "custom", label: "กำหนดเอง / ตามงวด" },
];

const billingLabel = (value) => BILLING_OPTIONS.find((o) => o.value === value)?.label || "—";

/* ---- ระดับความเร่งด่วนของวันหมดสัญญา — หัวใจของ effect สีบนหน้านี้ ---- */
const LEVEL = {
  expired: {
    rank: 0,
    label: "หมดอายุแล้ว",
    accent: "#dc2626",
    rowBg: "linear-gradient(90deg, rgba(220, 38, 38, 0.16) 0%, rgba(220, 38, 38, 0.05) 42%, rgba(255,255,255,0) 78%)",
    chipBg: "rgba(220, 38, 38, 0.16)",
    chipColor: "#991b1b",
    icon: "fa-circle-xmark",
  },
  critical: {
    rank: 1,
    label: "วิกฤต ใกล้หมดมาก",
    accent: "#f97316",
    rowBg: "linear-gradient(90deg, rgba(249, 115, 22, 0.20) 0%, rgba(249, 115, 22, 0.06) 42%, rgba(255,255,255,0) 78%)",
    chipBg: "rgba(249, 115, 22, 0.20)",
    chipColor: "#c2410c",
    icon: "fa-triangle-exclamation",
  },
  warning: {
    rank: 2,
    label: "ใกล้หมดสัญญา",
    accent: "#f59e0b",
    rowBg: "linear-gradient(90deg, rgba(245, 158, 11, 0.16) 0%, rgba(245, 158, 11, 0.05) 42%, rgba(255,255,255,0) 78%)",
    chipBg: "rgba(245, 158, 11, 0.18)",
    chipColor: "#b45309",
    icon: "fa-hourglass-half",
  },
  safe: {
    rank: 3,
    label: "ยังอยู่ในอายุสัญญา",
    accent: "#10b981",
    rowBg: "transparent",
    chipBg: "rgba(16, 185, 129, 0.14)",
    chipColor: "#047857",
    icon: "fa-circle-check",
  },
  none: {
    rank: 4,
    label: "ไม่ระบุวันสิ้นสุด",
    accent: "#cbd5e1",
    rowBg: "transparent",
    chipBg: "rgba(148, 163, 184, 0.20)",
    chipColor: "#64748b",
    icon: "fa-circle-minus",
  },
};

// วันเตือนของแต่ละสัญญา ตั้งเองได้ ถ้าไม่ตั้งใช้ 60 วันตามค่ามาตรฐาน
const noticeDaysOf = (item) => {
  const value = Number(item?.notice_days);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_NOTICE_DAYS;
};

// ครึ่งแรกของช่วงเตือนถือว่าวิกฤต (ค่ามาตรฐาน 60 วัน -> 30 วันสุดท้ายเป็นสีส้มกะพริบ)
const levelOf = (item, today) => {
  if (!item.end_date) return { key: "none", ...LEVEL.none, daysLeft: null, notice: noticeDaysOf(item) };
  const notice = noticeDaysOf(item);
  const daysLeft = daysBetween(today, item.end_date);
  if (item.status === "Cancelled") return { key: "none", ...LEVEL.none, daysLeft, notice };
  let key = "safe";
  if (daysLeft < 0) key = "expired";
  else if (daysLeft <= Math.max(1, Math.round(notice / 2))) key = "critical";
  else if (daysLeft <= notice) key = "warning";
  return { key, ...LEVEL[key], daysLeft, notice };
};

const daysText = (level) => {
  if (level.daysLeft === null) return "ไม่ระบุ";
  if (level.daysLeft < 0) return `เลยกำหนด ${Math.abs(level.daysLeft)} วัน`;
  if (level.daysLeft === 0) return "หมดสัญญาวันนี้";
  return `เหลือ ${level.daysLeft} วัน`;
};

/* ------------------------------------------------------------------ */
/* form                                                                */
/* ------------------------------------------------------------------ */

const emptyForm = {
  uid: "",
  ma_code: "",
  project_name: "",
  customer_name: "",
  status: "Active",
  start_date: "",
  end_date: "",
  contract_amount: "",
  billing_cycle: "yearly",
  notice_days: String(DEFAULT_NOTICE_DAYS),
  auto_renew: false,
  sla: "",
  service_hours: "",
  scope: "",
  note: "",
  is_active: true,
};

const sectionCardStyle = { background: "#ffffff", border: "1px solid rgba(18, 125, 255, 0.10)", borderRadius: 24, boxShadow: "0 14px 32px rgba(12, 118, 229, 0.06)" };

/* ---- ชิ้นส่วนฟอร์ม: จัดกลุ่มเป็นการ์ดต่อหมวด อ่านง่ายและดูสะอาดกว่าการไล่ field ยาว ๆ ---- */

// หัวข้อหมวด มีเลขลำดับกำกับ ให้สายตาไล่ลงมาเป็นขั้น
const FormSection = ({ step, title, hint, children }) => (
  <section className="ma-form-card">
    <header className="d-flex align-items-start gap-3 mb-3">
      <span className="ma-form-step">{step}</span>
      <div style={{ minWidth: 0 }}>
        <h6 className="mb-0 fw-bold" style={{ color: "#12314a", fontSize: 15, letterSpacing: "-0.01em" }}>{title}</h6>
        {hint ? <p className="mb-0" style={{ fontSize: 12, color: "#8296a8" }}>{hint}</p> : null}
      </div>
    </header>
    <div className="row g-3 g-md-4">{children}</div>
  </section>
);

// ห่อ label + input ให้ระยะห่างและขนาดตัวอักษรเท่ากันทุกช่อง
const Field = ({ col = "col-md-4", label, hint, must, children }) => (
  <div className={col}>
    <label className="ma-label">
      {label}
      {must ? <span className="ma-required">{required}</span> : null}
    </label>
    {children}
    {hint ? <p className="ma-hint">{hint}</p> : null}
  </div>
);

// รหัสสัญญา MA ระบบออกให้อัตโนมัติ: MA + ปีเดือนของวันที่เริ่มสัญญา + running 3 หลัก เช่น MA-202606002
const previewMaCode = (startDate) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(startDate || ""));
  return m ? `MA-${m[1]}${m[2]}xxx` : "MA-yyyymmxxx";
};

const ContractModal = ({ show, onHide, editData, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const today = todayIso();

  useEffect(() => {
    if (!show) return;
    setForm({
      ...emptyForm,
      uid: editData?.uid || "",
      ma_code: editData?.ma_code || "",
      project_name: editData?.project_name || "",
      customer_name: editData?.customer_name || "",
      status: editData?.status || "Active",
      start_date: editData?.start_date || "",
      end_date: editData?.end_date || "",
      contract_amount: editData ? String(Number(editData.contract_amount) || 0) : "",
      billing_cycle: editData?.billing_cycle || "yearly",
      notice_days: String(editData?.notice_days ?? DEFAULT_NOTICE_DAYS),
      auto_renew: !!editData?.auto_renew,
      sla: editData?.sla || "",
      service_hours: editData?.service_hours || "",
      scope: editData?.scope || "",
      note: editData?.note || "",
      is_active: editData ? editData.is_active !== false : true,
    });
  }, [editData, show]);

  const set = (key) => (event) => setForm((c) => ({ ...c, [key]: event.target.value }));

  // พรีวิวสถานะอายุสัญญาแบบสด ๆ ระหว่างกรอกฟอร์ม จะได้เห็นทันทีว่าเข้าเกณฑ์เตือนไหม
  const preview = useMemo(() => {
    if (!form.end_date) return null;
    const level = levelOf({ end_date: form.end_date, notice_days: Number(form.notice_days), status: form.status }, today);
    const span = form.start_date && form.end_date ? daysBetween(form.start_date, form.end_date) + 1 : null;
    return { level, span };
  }, [form.end_date, form.start_date, form.notice_days, form.status, today]);

  return (
    <Modal show={show} onHide={onHide} centered size="xl" contentClassName="border-0 shadow-lg overflow-hidden ma-form-modal" dialogClassName="modal-dialog-scrollable">
      <Modal.Header closeButton className="border-0 px-4 px-md-5 pt-4 pb-4 ma-form-head">
        <div>
          <p className="mb-1 fw-semibold" style={{ fontSize: 11.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "#5b8fc4" }}>
            {editData ? "แก้ไขข้อมูล" : "สร้างรายการใหม่"}
          </p>
          <Modal.Title className="fw-bold mb-0" style={{ color: "#0d2a41", fontSize: "1.5rem", letterSpacing: "-0.02em" }}>
            {editData ? form.project_name || "แก้ไขสัญญา MA" : "สัญญาบำรุงรักษา (MA)"}
          </Modal.Title>
          <p className="mb-0 mt-1" style={{ fontSize: 12.5, color: "#728ea6" }}>
            กรอกข้อมูลตามหมวดด้านล่าง ช่องที่มีเครื่องหมาย <span style={{ color: "#e0284b", fontWeight: 700 }}>*</span> จำเป็นต้องกรอก
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="px-3 px-md-5 py-4 ma-form-body">
        <FormSection step="1" title="ข้อมูลสัญญา" hint="ระบุว่าเป็นสัญญาของโปรเจกต์ไหน ทำกับใคร">
          <Field col="col-12 col-md-3" label="รหัสสัญญา MA" hint={form.uid ? undefined : "ออกให้อัตโนมัติจากวันที่เริ่มสัญญา"}>
            <input
              type="text"
              className="form-control ma-input text-uppercase"
              value={form.ma_code || previewMaCode(form.start_date)}
              readOnly
              disabled
              title="ระบบออกรหัสให้อัตโนมัติ (MA-ปีเดือน + running 3 หลัก)"
              style={{ background: "#f1f5f9", color: form.ma_code ? undefined : "#94a3b8", cursor: "not-allowed" }}
            />
          </Field>
          <Field col="col-12 col-md-9" label="ชื่อโปรเจกต์" must>
            <input type="text" className="form-control ma-input" value={form.project_name} onChange={set("project_name")} placeholder="เช่น ระบบ HR ภายในองค์กร" />
          </Field>
          <Field col="col-12 col-md-3" label="สถานะสัญญา">
            <Select className="form-control ma-input" value={form.status} options={STATUS_OPTIONS} default={false} placeholder="เลือกสถานะ" onChange={(value) => setForm((c) => ({ ...c, status: value || "Active" }))} />
          </Field>
          <Field col="col-12 col-md-9" label="บริษัท / ลูกค้า" must>
            <input type="text" className="form-control ma-input" value={form.customer_name} onChange={set("customer_name")} placeholder="เช่น บริษัท ตัวอย่าง จำกัด" />
          </Field>
        </FormSection>

        <FormSection step="2" title="ระยะเวลาและมูลค่า" hint="วันที่สิ้นสุดคือตัวกำหนดสีแจ้งเตือนใกล้หมดสัญญา">
          <Field col="col-6 col-md-3" label="วันที่เริ่มสัญญา" must>
            <DateInput className="form-control ma-input" value={form.start_date} max={form.end_date || undefined} onChange={set("start_date")} />
          </Field>
          <Field col="col-6 col-md-3" label="วันที่สิ้นสุดสัญญา" must>
            <DateInput className="form-control ma-input" value={form.end_date} min={form.start_date || undefined} onChange={set("end_date")} />
          </Field>
          <Field col="col-6 col-md-3" label="ราคา / มูลค่าสัญญา" must>
            <div className="input-group ma-group">
              <span className="input-group-text ma-affix">฿</span>
              <input type="number" min="0" step="0.01" className="form-control ma-input text-end" value={form.contract_amount} onChange={set("contract_amount")} placeholder="0.00" />
            </div>
          </Field>
          <Field col="col-6 col-md-3" label="รอบการวางบิล">
            <Select className="form-control ma-input" value={form.billing_cycle} options={BILLING_OPTIONS} default={false} placeholder="เลือกรอบวางบิล" onChange={(value) => setForm((c) => ({ ...c, billing_cycle: value }))} />
          </Field>

          <Field col="col-12 col-md-3" label="แจ้งเตือนล่วงหน้า">
            <div className="input-group ma-group">
              <input type="number" min="0" max="3650" className="form-control ma-input" value={form.notice_days} onChange={set("notice_days")} />
              <span className="input-group-text ma-affix">วันก่อนหมดสัญญา</span>
            </div>
          </Field>
          <div className="col-12 col-md-9">
            <label className="ma-label">ตัวเลือกสัญญา</label>
            <div className="d-flex flex-wrap gap-2">
              <label className={`ma-toggle${form.auto_renew ? " is-on" : ""}`}>
                <input type="checkbox" checked={form.auto_renew} onChange={(e) => setForm((c) => ({ ...c, auto_renew: e.target.checked }))} />
                <i className="fa-solid fa-rotate" />
                <span>ต่ออายุสัญญาอัตโนมัติ</span>
              </label>
              <label className={`ma-toggle${form.is_active ? " is-on" : ""}`}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((c) => ({ ...c, is_active: e.target.checked }))} />
                <i className="fa-solid fa-circle-check" />
                <span>เปิดใช้งานสัญญานี้</span>
              </label>
            </div>
          </div>

          {preview && (
            <div className="col-12">
              <div className="ma-preview" style={{ background: preview.level.chipBg, borderColor: `${preview.level.accent}55` }}>
                <span className="ma-preview-icon" style={{ background: `${preview.level.accent}22`, color: preview.level.chipColor }}>
                  <i className={`fa-solid ${preview.level.icon}`} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold" style={{ color: preview.level.chipColor, fontSize: 14.5 }}>
                    {preview.level.label} · {daysText(preview.level)}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#5b7285" }}>
                    สิ้นสุด {showDate(form.end_date)}
                    {preview.span !== null && preview.span > 0 ? ` · อายุสัญญารวม ${preview.span} วัน` : ""}
                    {` · เตือนเมื่อเหลือ ≤ ${noticeDaysOf({ notice_days: Number(form.notice_days) })} วัน`}
                  </div>
                </div>
              </div>
            </div>
          )}
        </FormSection>

        <FormSection step="3" title="เงื่อนไขบริการ" hint="ไม่บังคับ ระบุระดับการให้บริการและช่วงเวลาที่รับงานตามสัญญา">
          <Field col="col-12 col-md-6" label="SLA">
            <div className="input-group ma-group">
              <span className="input-group-text ma-affix ma-affix-icon"><i className="fa-solid fa-stopwatch fa-fw" /></span>
              <input type="text" className="form-control ma-input" value={form.sla} onChange={set("sla")} placeholder="เช่น ตอบกลับใน 4 ชม. / on-site ใน 1 วันทำการ" />
            </div>
          </Field>
          <Field col="col-12 col-md-6" label="ช่วงเวลาให้บริการ">
            <div className="input-group ma-group">
              <span className="input-group-text ma-affix ma-affix-icon"><i className="fa-solid fa-clock fa-fw" /></span>
              <input type="text" className="form-control ma-input" value={form.service_hours} onChange={set("service_hours")} placeholder="เช่น จ-ศ 9:00-18:00" />
            </div>
          </Field>
        </FormSection>

        <FormSection step="4" title="รายละเอียดเพิ่มเติม" hint="บันทึกขอบเขตงานและหมายเหตุที่ต้องใช้อ้างอิงภายหลัง">
          <Field col="col-12 col-md-6" label="ขอบเขตงาน MA">
            <textarea className="form-control ma-input" rows={4} value={form.scope} onChange={set("scope")} placeholder="งานที่ครอบคลุมในสัญญานี้" />
          </Field>
          <Field col="col-12 col-md-6" label="หมายเหตุ">
            <textarea className="form-control ma-input" rows={4} value={form.note} onChange={set("note")} placeholder="ข้อตกลงเพิ่มเติม เงื่อนไขพิเศษ ฯลฯ" />
          </Field>
        </FormSection>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 px-md-5 py-3 ma-form-foot">
        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 gap-sm-3 w-100">
          <span className="me-auto" style={{ fontSize: 12, color: "#8296a8" }}>
            <i className="fa-solid fa-shield-halved me-2" style={{ color: "#a9bed0" }} />
            ข้อมูลจะถูกบันทึกหลังกดยืนยัน
          </span>
          <button type="button" className="btn ma-btn-ghost px-4" onClick={onHide}>ยกเลิก</button>
          <button type="button" className="btn ma-btn-primary px-4" onClick={() => onSave(form)}>
            <i className="fa-solid fa-floppy-disk me-2" />
            {editData ? "บันทึกการแก้ไข" : "สร้างสัญญา"}
          </button>
        </div>
      </Modal.Footer>

      <style>{`
        .ma-form-modal { border-radius: 20px !important; }
        .ma-form-head {
          background: linear-gradient(135deg, #f4f9ff 0%, #eaf3fc 52%, #f7fbff 100%);
          border-bottom: 1px solid rgba(18, 125, 255, 0.12) !important;
        }
        .ma-form-body { background: #f6f9fc; }
        .ma-form-foot {
          background: #ffffff;
          border-top: 1px solid rgba(18, 125, 255, 0.12) !important;
        }

        .ma-form-card {
          background: #ffffff;
          border: 1px solid rgba(18, 125, 255, 0.10);
          border-radius: 18px;
          padding: 22px 22px 24px;
          box-shadow: 0 2px 10px rgba(15, 40, 70, 0.04);
        }
        .ma-form-card + .ma-form-card { margin-top: 18px; }

        .ma-form-step {
          flex-shrink: 0;
          width: 30px; height: 30px;
          border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; color: #0c76e5;
          background: rgba(12, 118, 229, 0.10);
          border: 1px solid rgba(12, 118, 229, 0.18);
        }

        .ma-label {
          display: block;
          font-size: 12.5px;
          font-weight: 600;
          color: #56718a;
          margin-bottom: 7px;
          letter-spacing: 0.01em;
        }
        .ma-required { margin-left: 3px; }
        .ma-hint { margin: 6px 0 0; font-size: 11.5px; color: #93a7b8; line-height: 1.45; }

        /* ช่องกรอกทุกชนิดสูงเท่ากัน ขอบบางและ focus เป็นวงแสงฟ้าจาง */
        .ma-form-modal .ma-input {
          border-radius: 12px;
          border: 1px solid #dbe5ee;
          background: #fbfdff;
          color: #17364d;
          font-size: 14px;
          padding: 10px 14px;
          box-shadow: none;
          transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .ma-form-modal textarea.ma-input { line-height: 1.6; }
        .ma-form-modal .ma-input::placeholder { color: #adbecd; }
        .ma-form-modal .ma-input:hover { border-color: #c4d6e6; }
        .ma-form-modal .ma-input:focus,
        .ma-form-modal .ma-input:focus-within {
          background: #ffffff;
          border-color: #6fb0f5;
          box-shadow: 0 0 0 3.5px rgba(12, 118, 229, 0.12);
        }

        /* input-group: กรอบ มุมโค้ง และวงแสง focus อยู่ที่ตัว group ใบเดียว
           ส่วนไอคอนกับช่องกรอกข้างในโปร่งใสไร้ขอบ จึงเห็นเป็นช่องเดียวกันสนิท */
        .ma-form-modal .ma-group {
          flex-wrap: nowrap;
          align-items: stretch;
          border: 1px solid #dbe5ee;
          border-radius: 12px;
          background: #fbfdff;
          overflow: hidden;
          transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .ma-form-modal .ma-group:hover { border-color: #c4d6e6; }
        .ma-form-modal .ma-group:focus-within {
          background: #ffffff;
          border-color: #6fb0f5;
          box-shadow: 0 0 0 3.5px rgba(12, 118, 229, 0.12);
        }
        .ma-form-modal .ma-group > .ma-affix,
        .ma-form-modal .ma-group > .ma-input {
          border: 0 !important;
          border-radius: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
          margin: 0 !important;
          min-height: 42px;
        }
        .ma-form-modal .ma-group > .ma-affix {
          display: inline-flex;
          align-items: center;
          color: #90a6b9;
          font-size: 13px;
          font-weight: 600;
          padding: 0 12px;
          user-select: none;
        }
        .ma-form-modal .ma-group > .ma-affix-icon { padding: 0 10px 0 14px; font-size: 13.5px; }
        /* หน่วยที่ต่อท้าย (เช่น "วันก่อนหมดสัญญา") คั่นด้วยเส้นบางแทนการเปลี่ยนพื้นหลัง */
        .ma-form-modal .ma-group > .ma-affix:last-child {
          border-left: 1px solid #e7eef5 !important;
          padding: 0 14px;
          font-weight: 500;
          font-size: 12.5px;
        }
        /* ช่องกรอกที่มีไอคอนนำหน้า ไม่ต้องเว้นซ้ายซ้ำอีก */
        .ma-form-modal .ma-group > .ma-affix + .ma-input { padding-left: 2px; }
        .ma-form-modal .ma-group > .ma-input:first-child { padding-left: 14px; }

        /* toggle แบบชิป กดทั้งใบได้ ชัดกว่าสวิตช์เล็ก ๆ */
        .ma-toggle {
          display: inline-flex; align-items: center; gap: 9px;
          padding: 10px 16px;
          border-radius: 12px;
          border: 1px solid #dbe5ee;
          background: #fbfdff;
          color: #6d879e;
          font-size: 13px; font-weight: 600;
          cursor: pointer;
          transition: all .15s ease;
        }
        .ma-toggle input { position: absolute; opacity: 0; pointer-events: none; }
        .ma-toggle i { font-size: 13px; opacity: .55; }
        .ma-toggle:hover { border-color: #c4d6e6; }
        .ma-toggle.is-on {
          border-color: rgba(12, 118, 229, 0.45);
          background: rgba(12, 118, 229, 0.08);
          color: #0b5fb8;
          box-shadow: inset 0 0 0 1px rgba(12, 118, 229, 0.10);
        }
        .ma-toggle.is-on i { opacity: 1; }

        /* Select ของโปรเจกต์ตั้ง top ไว้ 40px แบบ inline ซึ่งสั้นไปสำหรับช่องที่สูงขึ้น */
        .ma-form-modal #select-tool {
          top: calc(100% + 6px) !important;
          border-radius: 12px !important;
          border-color: #dbe5ee !important;
          box-shadow: 0 12px 28px rgba(15, 40, 70, 0.14);
        }

        .ma-preview {
          display: flex; align-items: center; gap: 14px;
          padding: 14px 18px;
          border-radius: 14px;
          border: 1px solid transparent;
        }
        .ma-preview-icon {
          flex-shrink: 0;
          width: 40px; height: 40px;
          border-radius: 12px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 17px;
        }

        .ma-btn-ghost {
          border-radius: 12px;
          border: 1px solid #dbe5ee;
          background: #ffffff;
          color: #5b7285;
          font-weight: 600;
          padding: 10px 20px;
        }
        .ma-btn-ghost:hover { background: #f2f6fa; color: #3d566e; border-color: #c4d6e6; }
        .ma-btn-primary {
          border-radius: 12px;
          border: 0;
          background: linear-gradient(135deg, #1a8ae8 0%, #0c76e5 55%, #0a63c4 100%);
          color: #ffffff;
          font-weight: 700;
          padding: 10px 22px;
          box-shadow: 0 6px 16px rgba(12, 118, 229, 0.28);
        }
        .ma-btn-primary:hover { color: #ffffff; filter: brightness(1.06); box-shadow: 0 8px 20px rgba(12, 118, 229, 0.34); }

        @media (max-width: 767.98px) {
          .ma-form-card { padding: 18px 16px 20px; border-radius: 16px; }
          .ma-form-card + .ma-form-card { margin-top: 14px; }
        }
      `}</style>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* detail modal                                                        */
/* ------------------------------------------------------------------ */

const DetailRow = ({ label, value }) => (
  <div className="col-6 col-md-4">
    <p className="mb-0" style={{ fontSize: 11, color: "#6b8198" }}>{label}</p>
    <p className="fw-semibold mb-0" style={{ fontSize: 13.5, color: "#1b3950" }}>{value || "—"}</p>
  </div>
);

const DetailModal = ({ item, level, onHide, onEdit }) => (
  <Modal show={!!item} onHide={onHide} centered size="lg" contentClassName="border-0 shadow-lg overflow-hidden" dialogClassName="modal-dialog-scrollable">
    {item && (
      <>
        <Modal.Header closeButton className="border-0 p-4" style={{ background: `linear-gradient(135deg, ${level.chipBg} 0%, #e7f6ff 100%)` }}>
          <Modal.Title className="fw-bold" style={{ color: "#11324c", fontSize: "1.3rem" }}>
            {item.project_name}
            <div className="fw-normal" style={{ fontSize: 13, color: "#4b6376" }}>{item.customer_name || "ไม่ระบุลูกค้า"}</div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ background: "linear-gradient(180deg, #fcfeff 0%, #f5faff 100%)" }}>
          <div className="rounded-4 p-3 mb-3 d-flex align-items-center gap-3" style={{ background: level.chipBg, border: `1px solid ${level.accent}55` }}>
            <i className={`fa-solid ${level.icon}`} style={{ color: level.chipColor, fontSize: 22 }} />
            <div>
              <div className="fw-bold" style={{ color: level.chipColor, fontSize: 15 }}>{level.label} · {daysText(level)}</div>
              <div className="small" style={{ color: "#4b6376" }}>
                {showDate(item.start_date)} – {showDate(item.end_date)} · เตือนเมื่อเหลือ ≤ {level.notice} วัน
              </div>
            </div>
          </div>
          <div className="row g-3">
            <DetailRow label="รหัสสัญญา MA" value={item.ma_code} />
            <DetailRow label="สถานะ" value={STATUS_STYLE[item.status]?.label || item.status} />
            <DetailRow label="ราคา / มูลค่าสัญญา" value={money(item.contract_amount)} />
            <DetailRow label="รอบการวางบิล" value={billingLabel(item.billing_cycle)} />
            <DetailRow label="ต่ออายุอัตโนมัติ" value={item.auto_renew ? "ใช่" : "ไม่"} />
            <DetailRow label="SLA" value={item.sla} />
            <DetailRow label="ช่วงเวลาให้บริการ" value={item.service_hours} />
            <DetailRow label="จำนวนงวด" value={`${item.periods?.length || 0} งวด`} />
            {item.scope && (
              <div className="col-12">
                <p className="mb-0" style={{ fontSize: 11, color: "#6b8198" }}>ขอบเขตงาน MA</p>
                <p className="mb-0" style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{item.scope}</p>
              </div>
            )}
            {item.note && (
              <div className="col-12">
                <p className="mb-0" style={{ fontSize: 11, color: "#6b8198" }}>หมายเหตุ</p>
                <p className="mb-0" style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{item.note}</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 py-3">
          <button type="button" className="btn btn-secondary rounded-4 px-4 ms-auto" onClick={onHide}>ปิด</button>
          <button type="button" className="btn btn-primary rounded-4 px-4" onClick={() => onEdit(item)}>แก้ไขสัญญา</button>
        </Modal.Footer>
      </>
    )}
  </Modal>
);

/* ------------------------------------------------------------------ */
/* page                                                                */
/* ------------------------------------------------------------------ */

export default function MaContracts() {
  const today = todayIso();

  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [detailItem, setDetailItem] = useState(null);

  const getData = useCallback(async () => {
    loading(true);
    const result = await GET(API, { page: 0 });
    if (result?.status) setData(result.data || []);
    loading(false);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  // ผูกระดับความเร่งด่วน + ยอดค้างรับ เข้ากับแต่ละสัญญาไว้ก่อน แล้วค่อยกรอง/เรียง
  const rows = useMemo(
    () =>
      data.map((item) => {
        const outstanding = (item.periods || [])
          .filter((p) => p.payment_status !== "Paid")
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        return { ...item, level: levelOf(item, today), outstanding };
      }),
    [data, today]
  );

  const visibleRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const filtered = rows.filter((item) => {
      if (keyword) {
        const haystack = [item.ma_code, item.project_name, item.customer_name, item.sla, item.service_hours]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      if (statusFilter && item.status !== statusFilter) return false;
      return true;
    });

    // เรียงใกล้หมดสัญญาก่อนเสมอ สัญญาที่ไม่ระบุวันสิ้นสุดไปท้ายสุด
    return [...filtered].sort((a, b) => {
      if (a.level.daysLeft === null) return 1;
      if (b.level.daysLeft === null) return -1;
      return a.level.daysLeft - b.level.daysLeft;
    });
  }, [rows, search, statusFilter]);

  const stats = useMemo(() => {
    const counts = { expired: 0, critical: 0, warning: 0, safe: 0, none: 0 };
    rows.forEach((item) => {
      counts[item.level.key] += 1;
    });
    return {
      ...counts,
      totalCount: rows.length,
      totalAmount: rows.reduce((sum, item) => sum + (Number(item.contract_amount) || 0), 0),
    };
  }, [rows]);

  // รายการที่ยังไม่หมดแต่เข้าช่วงเตือนแล้ว เอาไปขึ้นแบนเนอร์บนสุด
  const alertList = useMemo(
    () =>
      rows
        .filter((item) => ["critical", "warning"].includes(item.level.key))
        .sort((a, b) => a.level.daysLeft - b.level.daysLeft),
    [rows]
  );

  const handleSave = async (form) => {
    if (!form.project_name?.trim()) return alert("", "warning", "แจ้งเตือน", "กรุณากรอกชื่อโปรเจกต์");
    if (!form.customer_name?.trim()) return alert("", "warning", "แจ้งเตือน", "กรุณากรอกชื่อบริษัท / ลูกค้า");
    if (!form.start_date) return alert("", "warning", "แจ้งเตือน", "กรุณาเลือกวันที่เริ่มสัญญา");
    if (!form.end_date) return alert("", "warning", "แจ้งเตือน", "กรุณาเลือกวันที่สิ้นสุดสัญญา");
    if (form.end_date < form.start_date) return alert("", "warning", "แจ้งเตือน", "วันที่สิ้นสุดสัญญาต้องไม่ก่อนวันที่เริ่มสัญญา");
    if (String(form.contract_amount).trim() === "" || !Number.isFinite(Number(form.contract_amount)))
      return alert("", "warning", "แจ้งเตือน", "กรุณากรอกราคา / มูลค่าสัญญา");
    if (Number(form.contract_amount) < 0) return alert("", "warning", "แจ้งเตือน", "ราคาสัญญาต้องไม่ติดลบ");
    if (!(await alert("", "info", "ยืนยัน", "ต้องการบันทึกสัญญานี้?", "บันทึก", "ยกเลิก"))) return;

    const body = {
      // ma_code ไม่ส่ง: backend ออกรหัสให้อัตโนมัติจาก start_date
      project_name: form.project_name.trim(),
      customer_name: form.customer_name.trim(),
      status: form.status,
      start_date: form.start_date,
      end_date: form.end_date,
      contract_amount: Number(form.contract_amount) || 0,
      billing_cycle: form.billing_cycle || undefined,
      notice_days: Number(form.notice_days) > 0 ? Math.round(Number(form.notice_days)) : DEFAULT_NOTICE_DAYS,
      auto_renew: !!form.auto_renew,
      sla: form.sla?.trim() || undefined,
      service_hours: form.service_hours?.trim() || undefined,
      scope: form.scope?.trim() || undefined,
      note: form.note?.trim() || undefined,
      is_active: !!form.is_active,
    };

    loading(true);
    const result = form.uid ? await PUT(`${API}/${form.uid}`, body) : await POST(API, body);
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "บันทึกสัญญาเรียบร้อย");
      setShowModal(false);
      setEditData(null);
      setDetailItem(null);
      getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleDelete = async (item) => {
    if (!(await alert("", "warning", "ลบสัญญา MA", `ต้องการลบสัญญา "${item.project_name}" และงวดทั้งหมด?`, "ลบ", "ยกเลิก"))) return;
    loading(true);
    const result = await DELETE(`${API}/${item.uid}`);
    loading(false);
    if (result?.status) {
      await alert("", "success", "สำเร็จ", "ลบสัญญาเรียบร้อย");
      setDetailItem(null);
      getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ลบไม่สำเร็จ");
    }
  };

  const openCreate = () => {
    setEditData(null);
    setShowModal(true);
  };
  const openEdit = (item) => {
    setDetailItem(null);
    setEditData(item);
    setShowModal(true);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
  };

  const summaryCards = [
    { label: "สัญญาทั้งหมด", value: stats.totalCount, color: "#0c76e5", icon: "fa-file-contract" },
    { label: `ใกล้หมดสัญญา ≤ ${DEFAULT_NOTICE_DAYS} วัน`, value: stats.warning + stats.critical, color: "#f59e0b", icon: "fa-hourglass-half", glow: stats.warning + stats.critical > 0 },
    { label: "หมดอายุแล้ว", value: stats.expired, color: "#dc2626", icon: "fa-circle-xmark", glow: stats.expired > 0 },
    { label: "มูลค่ารวมทุกสัญญา", value: money(stats.totalAmount), color: "#10b981", icon: "fa-sack-dollar" },
  ];

  return (
    <AppShell title="สัญญา MA" description="ทะเบียนสัญญาบำรุงรักษา (MA) พร้อมแจ้งเตือนสัญญาที่ใกล้ครบกำหนดล่วงหน้า">
      {/* ---- KPI ---- */}
      <div className="row g-3 mb-3">
        {summaryCards.map((card) => (
          <div className="col-6 col-xl-3" key={card.label}>
            <div
              className={`h-100 rounded-4 p-3 p-md-4 d-flex align-items-center gap-3 ma-kpi${card.glow ? " ma-kpi-glow" : ""}`}
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
                border: `1px solid ${card.glow ? `${card.color}66` : "rgba(18, 125, 255, 0.10)"}`,
                boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
                "--ma-glow": `${card.color}59`,
              }}
            >
              <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 12, background: `${card.color}1f`, color: card.color, fontSize: 18 }}>
                <i className={`fa-solid ${card.icon}`} />
              </div>
              <div style={{ minWidth: 0 }}>
                <p className="small text-secondary mb-1 text-truncate">{card.label}</p>
                <h3 className="h4 mb-0 fw-bold text-truncate" style={{ color: card.color }}>{card.value}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ---- แบนเนอร์เตือนสัญญาใกล้หมด ---- */}
      {alertList.length > 0 && (
        <div className="rounded-4 p-3 p-md-4 mb-3 ma-alert-banner" style={{ background: "linear-gradient(135deg, rgba(249, 115, 22, 0.14) 0%, rgba(245, 158, 11, 0.08) 55%, rgba(255, 255, 255, 0.9) 100%)", border: "1px solid rgba(245, 158, 11, 0.45)" }}>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <i className="fa-solid fa-bell ma-shake" style={{ color: "#c2410c", fontSize: 18 }} />
            <span className="fw-bold" style={{ color: "#9a3412", fontSize: 15 }}>
              มี {alertList.length} สัญญาใกล้ครบกำหนดภายใน {DEFAULT_NOTICE_DAYS} วัน
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {alertList.slice(0, 6).map((item) => (
              <button
                key={item.uid}
                type="button"
                className="text-start rounded-3 px-3 py-2 border-0"
                style={{ background: "#ffffff", boxShadow: `inset 4px 0 0 ${item.level.accent}, 0 2px 8px rgba(15,23,42,0.06)`, minWidth: 210 }}
                onClick={() => setDetailItem(item)}
              >
                <div className="fw-bold text-truncate" style={{ fontSize: 13, color: "#1b3950" }}>{item.project_name}</div>
                <div className="text-truncate" style={{ fontSize: 11, color: "#6b8198" }}>{item.customer_name || "—"}</div>
                <div className="fw-bold" style={{ fontSize: 12, color: item.level.chipColor }}>
                  <i className={`fa-solid ${item.level.icon} me-1`} />{daysText(item.level)} · {showDate(item.end_date)}
                </div>
              </button>
            ))}
            {alertList.length > 6 && (
              <div className="d-flex align-items-center px-2 fw-semibold" style={{ fontSize: 12, color: "#9a3412" }}>
                และอีก {alertList.length - 6} สัญญา
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Filters ---- */}
      <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-3 gap-3">
        <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack">
          <input
            type="text"
            className="form-control"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && setSearch(searchInput)}
            placeholder="ค้นหาโปรเจกต์ ลูกค้า รหัส MA..."
            style={{ width: 240, borderRadius: 12 }}
          />
          <div style={{ width: 190 }}>
            <Select className="form-control" value={statusFilter} options={STATUS_OPTIONS} placeholder="ทุกสถานะ" onChange={(value) => setStatusFilter(value)} />
          </div>
          <button type="button" className="btn btn-primary px-4" onClick={() => setSearch(searchInput)} style={{ borderRadius: 12 }}>ค้นหา</button>
          <button type="button" className="btn btn-light px-4" onClick={clearFilters} style={{ borderRadius: 12, border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}>ล้างค่า</button>
        </div>
        <button type="button" className="btn btn-primary px-4 flex-shrink-0" style={{ borderRadius: 12 }} onClick={openCreate}>
          + สร้างสัญญา MA
        </button>
      </div>

      {/* ---- Table ---- */}
      <div className="p-2 p-md-3" style={sectionCardStyle}>
        <div className="table-responsive table-mobile-cards">
          <table className="table align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium">โปรเจกต์ / สัญญา</th>
                <th className="text-secondary fw-medium">บริษัท / ลูกค้า</th>
                <th className="text-secondary fw-medium text-center text-nowrap">เหลือกี่วัน</th>
                <th className="text-secondary fw-medium text-end text-nowrap">ราคา (บาท)</th>
                <th className="text-secondary fw-medium text-center">สถานะ</th>
                <th className="text-secondary fw-medium text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length > 0 ? (
                visibleRows.map((item) => {
                  const level = item.level;
                  const status = STATUS_STYLE[item.status] || STATUS_STYLE.Active;
                  const alarm = ["expired", "critical", "warning"].includes(level.key);
                  return (
                    <tr
                      key={item.uid}
                      className={`ma-row${alarm ? " ma-row-alarm" : ""}`}
                      style={{ background: level.rowBg, cursor: "pointer" }}
                      onClick={() => setDetailItem(item)}
                    >
                      <td data-label="โปรเจกต์" style={{ borderLeft: `4px solid ${level.accent}` }}>
                        <div className="fw-semibold text-truncate" style={{ color: "#132f46" }}>{item.project_name}</div>
                        <div className="small text-truncate d-flex align-items-center gap-2 mt-1">
                          {item.ma_code ? (
                            <span className="rounded-pill px-2 fw-bold flex-shrink-0" style={{ fontSize: 10.5, lineHeight: "18px", background: "rgba(12, 117, 230, 0.12)", color: "#0b4f84" }}>{item.ma_code}</span>
                          ) : (
                            <span className="text-secondary">ไม่มีรหัสสัญญา</span>
                          )}
                          {item.auto_renew ? <span className="text-secondary">ต่ออายุอัตโนมัติ</span> : null}
                        </div>
                      </td>
                      <td data-label="บริษัท / ลูกค้า">
                        <div className="fw-medium text-truncate">{item.customer_name || "—"}</div>
                        <div className="small text-secondary text-truncate">{[item.sla, item.service_hours].filter(Boolean).join(" · ") || "—"}</div>
                      </td>
                      <td data-label="เหลือกี่วัน" className="text-center text-nowrap">
                        <span
                          className={`rounded-pill px-3 py-1 fw-bold d-inline-block${level.key === "critical" || level.key === "expired" ? " ma-pulse" : ""}`}
                          style={{ fontSize: 12, background: level.chipBg, color: level.chipColor }}
                        >
                          <i className={`fa-solid ${level.icon} me-1`} />{daysText(level)}
                        </span>
                      </td>
                      <td data-label="ราคา" className="text-end text-nowrap">
                        <div className="fw-bold" style={{ color: "#132f46" }}>{money(item.contract_amount)}</div>
                        {item.outstanding > 0 ? <div className="small" style={{ color: "#b45309" }}>ค้างรับ {money(item.outstanding)}</div> : null}
                      </td>
                      <td data-label="สถานะ" className="text-center text-nowrap">
                        <span className="rounded-pill px-3 py-1 small fw-semibold d-inline-block" style={status}>{status.label}</span>
                        {item.is_active === false ? <div className="small text-secondary mt-1">ปิดใช้งาน</div> : null}
                      </td>
                      <td data-label="จัดการ" className="text-center text-nowrap" onClick={(event) => event.stopPropagation()}>
                        <button className="btn btn-sm btn-outline-secondary rounded-3 me-1" title="แก้ไข" onClick={() => openEdit(item)}>
                          <i className="fa-solid fa-pen" />
                        </button>
                        <button className="btn btn-sm btn-outline-danger rounded-3" title="ลบ" onClick={() => handleDelete(item)}>
                          <i className="fa-solid fa-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-secondary py-5">
                    {rows.length ? "ไม่พบสัญญาที่ตรงกับเงื่อนไขที่เลือก" : "ยังไม่มีสัญญา MA — กด “สร้างสัญญา MA” เพื่อเริ่มบันทึก"}
                  </td>
                </tr>
              )}
            </tbody>
            {visibleRows.length > 0 && (
              <tfoot>
                <tr style={{ background: "#f3f9ff" }}>
                  <td colSpan="3" className="fw-semibold text-end" style={{ color: "#0b4f84" }}>รวม {visibleRows.length} สัญญา</td>
                  <td className="text-end fw-bold" style={{ color: "#0b4f84" }}>
                    {money(visibleRows.reduce((sum, item) => sum + (Number(item.contract_amount) || 0), 0))}
                  </td>
                  <td colSpan="2" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <ContractModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditData(null);
        }}
        editData={editData}
        onSave={handleSave}
      />

      <DetailModal
        item={detailItem}
        level={detailItem?.level || LEVEL.none}
        onHide={() => setDetailItem(null)}
        onEdit={openEdit}
      />

      <style>{`
        .ma-row { transition: filter .15s ease, transform .15s ease; }
        .ma-row:hover { filter: brightness(0.97); }
        .ma-row-alarm td { border-bottom-color: rgba(245, 158, 11, 0.25) !important; }
        .ma-pulse { animation: ma-pulse 1.6s ease-in-out infinite; }
        @keyframes ma-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.35); }
          50% { transform: scale(1.04); box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
        }
        .ma-kpi { transition: transform .15s ease, box-shadow .15s ease; }
        .ma-kpi:hover { transform: translateY(-3px); }
        .ma-kpi-glow { animation: ma-glow 2.2s ease-in-out infinite; }
        @keyframes ma-glow {
          0%, 100% { box-shadow: 0 14px 28px rgba(12, 118, 229, 0.06); }
          50% { box-shadow: 0 14px 28px rgba(12, 118, 229, 0.06), 0 0 0 5px var(--ma-glow); }
        }
        .ma-alert-banner { animation: ma-banner-in .35s ease both; }
        @keyframes ma-banner-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
        .ma-shake { animation: ma-shake 2.4s ease-in-out infinite; transform-origin: 50% 0; }
        @keyframes ma-shake {
          0%, 82%, 100% { transform: rotate(0); }
          86% { transform: rotate(-14deg); }
          90% { transform: rotate(12deg); }
          94% { transform: rotate(-8deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ma-pulse, .ma-kpi-glow, .ma-alert-banner, .ma-shake { animation: none !important; }
        }
        @media (max-width: 767px) {
          .ma-row td:first-child { border-left-width: 6px !important; }
        }
      `}</style>
    </AppShell>
  );
}
